import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;

// The calm weekly note: auto-sends Monday morning IST; Div can send or preview anytime.
export default function WeeklyDigestCard() {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hq/digest/status`).then(({ data }) => setLast(data.last)).catch(() => {});
  }, []);

  const run = async (preview) => {
    if (busy) return;
    if (!preview && !window.confirm("Send the weekly note to every active client now?")) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/hq/digest/run`, { preview });
      setResult(data);
      if (!preview && data.results?.length) setLast({ week: data.week, sent_at: new Date().toISOString(), count: data.results.length });
    } catch (e) {
      setResult({ error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 md:p-6" data-testid="hq-digest-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-white/40">
          <Mail className="w-3.5 h-3.5" /> Weekly Client Digest
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => run(true)}
            disabled={busy}
            className="rounded-full border border-white/15 px-3.5 py-1.5 text-[12px] text-white/60 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50"
            data-testid="hq-digest-preview"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={() => run(false)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0c111c] px-3.5 py-1.5 text-[12px] font-semibold hover:bg-white/90 transition-colors disabled:opacity-50"
            data-testid="hq-digest-send"
          >
            <Send className="w-3 h-3" /> {busy ? "Working\u2026" : "Send now"}
          </button>
        </div>
      </div>
      <p className="mt-3 text-white/40 text-[12.5px] leading-[1.6]" data-testid="hq-digest-status">
        {last
          ? `Last sent ${fmtWhen(last.sent_at)} (${last.week}) to ${last.count} client${last.count > 1 ? "s" : ""}.`
          : "Not sent yet. Auto-sends every Monday morning to clients whose projects moved that week."}
      </p>
      {result && !result.error && (
        <div className="mt-4 space-y-2.5" data-testid="hq-digest-results">
          {result.results.length === 0 && (
            <p className="text-white/45 text-[12.5px]">No client had visible movement this week. Nothing to send.</p>
          )}
          {result.results.map((r) => (
            <div key={r.project_id} className="rounded-xl bg-white/[0.04] border border-white/[0.06] px-4 py-3">
              <div className="text-[11px] text-white/40">
                {r.title} · {r.email} {result.preview ? "· preview" : r.sent ? "· sent" : "· failed"}
              </div>
              <div className="mt-1 text-white/80 text-[13px] font-medium">{r.digest?.headline}</div>
              <div className="mt-0.5 text-white/50 text-[12.5px] leading-[1.6]">{r.digest?.note}</div>
            </div>
          ))}
        </div>
      )}
      {result?.error && (
        <p className="mt-3 text-[12.5px] text-amber-200/80">Something slipped. Try again in a moment.</p>
      )}
    </div>
  );
}
